import { useEffect, useMemo } from "react";
import {
  useDictionary,
  useHandleRequest,
} from "@fas/cpa-state-manager/redux/hooks";
import type { Option } from "@fas/cpa-state-manager/redux/reducers";
import { addNotification, useFormField } from "@fas/ui-framework";
import { useDispatch } from "react-redux";
import {
  getNatureOfTraffic,
  updateNatureOfTraffic,
} from "../../services/deprecatedApi";
import { NATURE_OF_TRAFFIC_MODAL } from "../../constants";

export type UseNatureOfTrafficModalHook = {
  open: boolean;
  isLoading: boolean;
  isLoadingOptions: boolean;
  isConfirm: boolean;
  onChangeIsConfirm: (arg0: { name: string; checked: boolean }) => any;
  onChangeNatureOfTraffic: (arg0: { name: string; checked: boolean }) => any;
  onClose: () => any;
  selected: string;
  tooltipConfirmButton: string;
  options: Option[];
};

function useValue<T>(key: string) {
  const { value, onChange } = useFormField<T>(NATURE_OF_TRAFFIC_MODAL, key);
  return {
    value,
    onChange,
  };
}

function getTooltipConfirmButton(isConfirm: boolean, selected: string): string {
  const tooltipMap: any = {
    isConfirm: {
      false: "Mark the confirm checkbox to continue",
    },
    isZeroSelected: {
      true: "Please specify nature of traffic",
    },
  };
  return (
    tooltipMap.isConfirm[String(isConfirm)] ||
    tooltipMap.isZeroSelected[String(selected.length === 0)] ||
    ""
  );
}

export const useNatureOfTrafficModal: () => UseNatureOfTrafficModalHook = () => {
  const dispatch = useDispatch();
  const { value: open, onChange: setOpen } = useValue<boolean>("open");
  const { value: isConfirm = false, onChange: setIsConfirm } =
      useValue<boolean>("isConfirm");
  const { value: initialSelected = "", onChange: onChangeInitialSelected } =
      useValue<string>("initialSelected");
  const { value: selected = "", onChange: onChangeSelected } =
      useValue<string>("selected");
  const { isLoading: isLoadingOptions, options } =
      useDictionary("natureOfTraffic");
  const additionalOptions: Option[] = useMemo(
    () =>
      (initialSelected ? [initialSelected] : [])
        .filter((value) => options.every((option) => option.value !== value))
        .map((value) => ({
          title: value,
          value,
        })),
    [initialSelected, options]
  );
  const {
    isLoading: isLoadingNaturesOfTraffic,
    changeLoading,
    onFail,
    onFinally,
  } = useHandleRequest(NATURE_OF_TRAFFIC_MODAL);
  const {
    isLoading,
    changeLoading: changeLoadingSave,
    onFail: onFailSave,
    onFinally: onFinallySave,
  } = useHandleRequest(`${NATURE_OF_TRAFFIC_MODAL}-update`);
  useEffect(() => {
    changeLoading(true);
    getNatureOfTraffic()
      .then((initSelected) => {
        onChangeInitialSelected(initSelected);
        onChangeSelected(initSelected);
      })
      .catch(onFail)
      .finally(onFinally);
  }, []);

  const saveTrafficSources: () => any = async () => {
    if (!isConfirm) return;
    changeLoadingSave(true);
    updateNatureOfTraffic(selected, isConfirm)
      .then(() => {
        dispatch(
          addNotification({
            severity: "success",
            message: "Nature of traffic updated successfully",
          })
        );
        setOpen(false);
      })
      .catch(onFailSave)
      .finally(onFinallySave);
  };

  return {
    tooltipConfirmButton: getTooltipConfirmButton(isConfirm, selected),
    isLoadingOptions: isLoadingNaturesOfTraffic || isLoadingOptions,
    isLoading,
    open: Boolean(open),
    isConfirm: Boolean(isConfirm),
    onChangeIsConfirm: (e) => setIsConfirm(e.checked),
    onClose: saveTrafficSources,
    selected,
    onChangeNatureOfTraffic: (e) => onChangeSelected(e.name),
    options: options.concat(additionalOptions),
  };
};
